import { useEffect, useRef, useState } from 'react'

import type { Appointment } from '../../api/timetable'
import { whatItIsAbout } from '../../api/timetable'
import {
  DAY_MINUTES,
  DEFAULT_LENGTH,
  type Laid,
  SNAP,
  WORKING_FROM,
  WORKING_UNTIL,
  dayKey,
  dayNumber,
  minuteAt,
  minutesInDay,
  overlap,
  readableDay,
  readableTime,
  snapped,
  todayKey,
  weekdayOf,
} from './week'

/**
 * The calendar itself: a time grid over one day or over seven.
 *
 * One component for both, because a day view *is* a week view with a single column — everything else
 * about the two is identical, and keeping them apart would be keeping two places for an appointment to be
 * drawn slightly differently.
 *
 * **The grid is the input.** Drawing over a day makes an appointment there; an appointment is dragged to another
 * hour or another day, and taken by its lower edge to make it longer. That is the whole point of
 * this component: an appointment is made where it belongs, not in a form that asks for a day and two
 * times in words. A form that does the same thing is still one control away — the gesture is the
 * shortcut and never the only way — but it is the second door now, not the first.
 *
 * Three things are structural rather than decorative:
 *
 * - **The whole day is there, and it scrolls.** No window of "eight to six" that an evening study
 *   group falls out of. What is drawn lighter is the teaching day, and it decides only where the
 *   grid stands when it opens.
 * - **Everything is placed from one number.** `--hour` is the height of an hour; the rule, the hour
 *   lines, the now-line and every appointment are positioned by multiplying minutes with it. Nothing is
 *   aligned by a margin that guesses at the height of something else.
 * - **An appointment is drawn in the zone it was spoken in.** An 8:00 lecture is at the top of Tuesday
 *   morning on a train through Portugal too.
 *
 * On a narrow screen the columns stack into a list — `timetable.css` does that, and there the
 * drawing is off: a drag raster on 390 points is worse than the control beside it.
 */
export interface GridProps {
  /** The days to draw, left to right. Seven of them for a week, one for a day. */
  readonly days: readonly string[]
  readonly appointments: readonly Appointment[]
  readonly openId: string | null
  /** A double click, or the control on the card: the whole page for this appointment. */
  readonly onOpen: (appointmentId: string) => void
  /** A single click: the card that says what it is, without leaving the week. */
  readonly onPeek: (appointment: Appointment, at: { x: number; y: number }) => void
  /** A stretch somebody drew over a day. What happens next is the area's business, not this one's. */
  readonly onDraw: (draft: Draft) => void
  /** An appointment dragged to another time, or taken longer by its edge. Both are one save. */
  readonly onMoved: (appointment: Appointment, from: number, to: number, day: string) => void
}

/**
 * Whether this press is one that draws, moves and stretches — or one that scrolls.
 *
 * **A finger scrolls, and nothing else.** Every gesture on this grid begins by capturing the
 * pointer, and a captured touch is a touch the browser will not scroll with: on a tablet held in
 * portrait the grid covers the whole surface, so every attempt to reach the evening was swallowed by
 * a drag that drew nothing. Which reads exactly like a page that has frozen.
 *
 * So drawing belongs to a mouse and a pen, both of which can press without the press being the only
 * way to move the page. Nothing is lost by it: every one of these gestures is a shortcut for
 * something with a visible control beside it — the control that adds an appointment, and the fields
 * in the appointment itself — which is the rule the whole of MyLib is built on. A phone does not
 * reach this component at all; it gets `Agenda`.
 */
function drawnWith(event: React.PointerEvent): boolean {
  return event.pointerType !== 'touch'
}

/** A stretch drawn on the grid: which day, from which minute to which, and where on the screen. */
export interface Draft {
  readonly day: string
  /** The last day of the stretch. The same as [day] for everything that fits in one. */
  readonly untilDay: string
  /** Whether it was drawn in the all-day strip rather than in the hours. */
  readonly allDay: boolean
  readonly from: number
  readonly to: number
  /** Where the pointer let go, so the little sheet can stand beside the stretch and not over it. */
  readonly at: { readonly x: number; readonly y: number }
}

/** What is being dragged right now, and what it has become so far. */
interface Dragging {
  readonly what: 'draw' | 'move' | 'resize'
  readonly appointment: Appointment | null
  readonly day: string
  readonly from: number
  readonly to: number
  /** How far into the appointment the pointer took hold, so it does not jump to its own top edge. */
  readonly grip: number
}

export function Grid({ days, appointments, openId, onOpen, onPeek, onDraw, onMoved }: GridProps) {
  const today = todayKey()

  const body = useRef<HTMLDivElement | null>(null)
  const columns = useRef<(HTMLDivElement | null)[]>([])
  const allDayCells = useRef<(HTMLDivElement | null)[]>([])
  const [dragging, setDragging] = useState<Dragging | null>(null)
  /*
   * The stretch being drawn across the all-day strip.
   *
   * Its own bit of state rather than a fourth kind of `Dragging`: it moves sideways over days
   * instead of downwards over minutes, and folding the two into one would be one function that has
   * to ask which axis it is on at every step.
   */
  const [spanning, setSpanning] = useState<{ from: string; to: string } | null>(null)
  /*
   * Whether the last press on an appointment actually moved it.
   *
   * An appointment is a button: the click is what opens it, and that is what keeps it reachable from the
   * keyboard and from anything that does not speak pointer events. The drag only has to say "this
   * one was not a click" — which is one flag rather than a second way of opening things.
   */
  const shifted = useRef(false)
  /** Which view the grid has already been scrolled into place for. See the effect below. */
  const settled = useRef('')
  const [now, setNow] = useState(() =>
    minutesInDay(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone),
  )

  // The now-line is the one thing here that moves by itself. Once a minute is precise enough for a
  // line a pixel wide, and it costs one timer for the whole grid.
  useEffect(() => {
    const tick = setInterval(() => {
      setNow(minutesInDay(new Date(), Intl.DateTimeFormat().resolvedOptions().timeZone))
    }, 60_000)
    return () => {
      clearInterval(tick)
    }
  }, [])

  /** How tall an hour is on this screen — the number every placement below is measured against. */
  const hourHeight = (): number => {
    const column = columns.current[0]
    return column === null || column === undefined ? 0 : column.getBoundingClientRect().height / 24
  }

  /*
   * Where the grid stands when it opens: at the teaching day, or a little above the earliest appointment
   * if something starts before it. Scrolling an element is not state, so this is what an effect is
   * actually for.
   */
  useEffect(() => {
    const pane = body.current
    const view = `${days[0] ?? ''}+${String(days.length)}`
    /*
      Once per view, and not on every render.
      
      `days` is built afresh each time, so this effect runs constantly — and scrolling on each of
      them would throw the grid back to the morning every time anything at all changed, the little
      sheet opening included.
    */
    if (pane === null || settled.current === view) {
      return
    }
    settled.current = view
    const earliest = appointments
      .filter((appointment) => !appointment.allDay)
      .map((appointment) => minutesInDay(new Date(appointment.startsAt), appointment.zone))
      .reduce((lowest, minute) => Math.min(lowest, minute), WORKING_FROM)
    pane.scrollTop = (Math.max(0, earliest - 30) / 60) * hourHeight()
  }, [days, appointments])

  /** Which day the pointer is over, so an appointment can be dragged into the next column. */
  const dayUnder = (x: number, fallback: string): string => {
    const at = columns.current.slice(0, days.length).findIndex((column) => {
      if (column === null || column === undefined) {
        return false
      }
      const box = column.getBoundingClientRect()
      return x >= box.left && x <= box.right
    })
    return at === -1 ? fallback : (days[at] ?? fallback)
  }

  const minuteUnder = (y: number): number => {
    const column = columns.current[0]
    if (column === null || column === undefined) {
      return 0
    }
    return minuteAt(y - column.getBoundingClientRect().top, hourHeight())
  }

  const startDrawing = (event: React.PointerEvent<HTMLDivElement>, day: string) => {
    // Only the empty surface draws. A press on an appointment is that appointment's own gesture.
    if (
      !drawnWith(event) ||
      event.button !== 0 ||
      (event.target as HTMLElement).closest('.cal__slot') !== null
    ) {
      return
    }
    const at = snapped(minuteUnder(event.clientY))
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragging({ what: 'draw', appointment: null, day, from: at, to: at, grip: 0 })
  }

  const startMoving = (
    event: React.PointerEvent<HTMLElement>,
    appointment: Appointment,
    what: 'move' | 'resize',
  ) => {
    if (!drawnWith(event) || event.button !== 0) {
      return
    }
    event.stopPropagation()
    const from = minutesInDay(new Date(appointment.startsAt), appointment.zone)
    const to = minutesInDay(new Date(appointment.endsAt), appointment.zone)
    event.currentTarget.setPointerCapture(event.pointerId)
    shifted.current = false
    setDragging({
      what,
      appointment,
      day: dayKey(new Date(appointment.startsAt), appointment.zone),
      from,
      to,
      grip: minuteUnder(event.clientY) - from,
    })
  }

  const keepDragging = (event: React.PointerEvent) => {
    if (dragging === null) {
      return
    }
    const minute = minuteUnder(event.clientY)
    if (dragging.what === 'draw') {
      setDragging({ ...dragging, to: snapped(minute) })
      return
    }
    if (dragging.what === 'resize') {
      const to = Math.max(dragging.from + SNAP, snapped(minute))
      shifted.current = shifted.current || to !== dragging.to
      setDragging({ ...dragging, to })
      return
    }
    const length = dragging.to - dragging.from
    const from = Math.min(DAY_MINUTES - length, Math.max(0, snapped(minute - dragging.grip)))
    const day = dayUnder(event.clientX, dragging.day)
    shifted.current = shifted.current || from !== dragging.from || day !== dragging.day
    setDragging({ ...dragging, day, from, to: from + length })
  }

  const letGo = (event: React.PointerEvent) => {
    if (dragging === null) {
      return
    }
    const drawn = dragging
    setDragging(null)

    if (drawn.what === 'draw') {
      const from = Math.min(drawn.from, drawn.to)
      const drawnTo = Math.max(drawn.from, drawn.to)
      // A click rather than a drag: a lecture's worth of time, which is what somebody meant.
      const to = drawnTo - from < SNAP ? Math.min(DAY_MINUTES, from + DEFAULT_LENGTH) : drawnTo
      onDraw({
        day: drawn.day,
        untilDay: drawn.day,
        allDay: false,
        from,
        to,
        at: { x: event.clientX, y: event.clientY },
      })
      return
    }

    // Nothing actually moved: this was a click, and the click below is what opens it.
    if (drawn.appointment === null || !shifted.current) {
      return
    }
    onMoved(drawn.appointment, drawn.from, drawn.to, drawn.day)
  }

  const indexOfDay = (day: string): number => Math.max(0, days.indexOf(day))

  /** Which day the pointer is over in the all-day strip. */
  const dayOverCells = (x: number, fallback: string): string => {
    const at = allDayCells.current.slice(0, days.length).findIndex((cell) => {
      if (cell === null || cell === undefined) {
        return false
      }
      const box = cell.getBoundingClientRect()
      return x >= box.left && x <= box.right
    })
    return at === -1 ? fallback : (days[at] ?? fallback)
  }

  /**
   * What belongs in the strip rather than in the hours.
   *
   * Anything marked all-day, and anything that simply does not end on the day it began: a block
   * session over three days has no place in a column of one of them.
   */
  const lastDayOf = (appointment: Appointment): string =>
    dayKey(new Date(new Date(appointment.endsAt).getTime() - 60_000), appointment.zone)

  const acrossDays = appointments.filter(
    (appointment) =>
      appointment.allDay ||
      dayKey(new Date(appointment.startsAt), appointment.zone) !== lastDayOf(appointment),
  )
  const timed = appointments.filter((appointment) => !acrossDays.includes(appointment))

  /*
   * The bars, stacked so that two overlapping ones do not lie on top of each other.
   *
   * The same idea as the tracks in a column, turned ninety degrees: each bar takes the first row in
   * which nothing else already covers the days it needs.
   */
  const bars: { what: Appointment; from: number; to: number; row: number }[] = []
  const rows: number[] = []
  acrossDays
    .filter(
      (appointment) =>
        days.includes(dayKey(new Date(appointment.startsAt), appointment.zone)) ||
        days.includes(lastDayOf(appointment)),
    )
    .forEach((appointment) => {
      const from = indexOfDay(dayKey(new Date(appointment.startsAt), appointment.zone))
      const to = indexOfDay(lastDayOf(appointment))
      let row = rows.findIndex((takenUntil) => takenUntil < from)
      if (row === -1) {
        row = rows.length
        rows.push(to)
      } else {
        rows[row] = to
      }
      bars.push({ what: appointment, from: Math.min(from, to), to: Math.max(from, to), row })
    })

  return (
    <div className="cal" style={{ '--columns': days.length } as React.CSSProperties}>
      {/*
        Everything is inside the one scrolling box — head, all-day strip and hours alike.

        That is what keeps the day names over their columns: a vertical scrollbar takes its width
        from whatever it scrolls, so a head *outside* the box is wider than the columns inside it by
        exactly that much, and the drift grows across the week. Sticky rows cost nothing and cannot
        come apart.
      */}
      <div className="cal__body" ref={body}>
        <div className="cal__top">
          <div className="cal__head">
            <span className="cal__corner" aria-hidden="true" />
            {days.map((day) => (
              <h3 className="cal__day-name" key={day} data-today={day === today}>
                <span className="cal__day-number">{dayNumber(day)}</span>
                <span className="cal__day-weekday">{weekdayOf(day)}</span>
              </h3>
            ))}
          </div>

          {/*
        The all-day strip — and the second place the grid is written on.
        
        It is always there, empty or not, because it is where a multi-day entry is drawn: press on a
        day, pull sideways, let go. A strip that only appeared once something was in it would be a
        strip nobody could put the first thing into.
      */}
          <div className="cal__allday">
            <span className="cal__allday-label">All day</span>
            {days.map((day, index) => (
              <div
                className="cal__allday-day"
                key={day}
                data-today={day === today}
                ref={(element) => {
                  allDayCells.current[index] = element
                }}
                onPointerDown={(event) => {
                  if (
                    !drawnWith(event) ||
                    event.button !== 0 ||
                    (event.target as HTMLElement).closest('.cal__bar') !== null
                  ) {
                    return
                  }
                  event.currentTarget.setPointerCapture(event.pointerId)
                  setSpanning({ from: day, to: day })
                }}
                onPointerMove={(event) => {
                  if (spanning !== null) {
                    setSpanning({ ...spanning, to: dayOverCells(event.clientX, spanning.to) })
                  }
                }}
                onPointerUp={(event) => {
                  if (spanning === null) {
                    return
                  }
                  const [first, last] = [spanning.from, spanning.to].sort()
                  setSpanning(null)
                  onDraw({
                    day: first ?? day,
                    untilDay: last ?? day,
                    allDay: true,
                    from: 0,
                    to: DAY_MINUTES,
                    at: { x: event.clientX, y: event.clientY },
                  })
                }}
                onPointerCancel={() => {
                  setSpanning(null)
                }}
              />
            ))}

            {bars.map((bar) => (
              <button
                type="button"
                className="cal__bar"
                key={bar.what.id}
                data-open={bar.what.id === openId}
                data-cancelled={bar.what.cancelled}
                style={{
                  gridColumn: `${String(bar.from + 2)} / ${String(bar.to + 3)}`,
                  gridRow: bar.row + 1,
                }}
                onClick={(event) => {
                  onPeek(bar.what, { x: event.clientX, y: event.clientY })
                }}
                onDoubleClick={() => {
                  onOpen(bar.what.id)
                }}
              >
                {bar.what.title}
              </button>
            ))}

            {spanning !== null && (
              <span
                className="cal__bar cal__bar--draft"
                aria-hidden="true"
                style={{
                  gridColumn: `${String(Math.min(indexOfDay(spanning.from), indexOfDay(spanning.to)) + 2)} / ${String(
                    Math.max(indexOfDay(spanning.from), indexOfDay(spanning.to)) + 3,
                  )}`,
                  gridRow: bars.length + 1,
                }}
              />
            )}
          </div>
        </div>

        <div className="cal__grid">
          <div className="cal__rule" aria-hidden="true">
            {Array.from({ length: 24 }, (_, hour) => (
              <span className="cal__hour" key={hour} style={at(hour * 60)}>
                {String(hour).padStart(2, '0')}
              </span>
            ))}
          </div>

          {days.map((day, index) => {
            const here = timed.filter(
              (appointment) => dayKey(new Date(appointment.startsAt), appointment.zone) === day,
            )
            /*
            Laid out from what is *stored*, never from what is being dragged.

            That is the whole of three complaints at once: nothing reflows under the pointer, two
            parallel appointments keep the order they had, and the thing being moved is drawn as a
            ghost in whichever column the pointer is actually over — instead of being stuck in the
            one it started in.
          */
            const laid = overlap(here, (appointment) => stored(appointment))
            const ghost =
              dragging !== null && dragging.appointment !== null && dragging.day === day
                ? dragging
                : null

            return (
              <div
                className="cal__column"
                key={day}
                data-today={day === today}
                ref={(element) => {
                  columns.current[index] = element
                }}
                onPointerDown={(event) => {
                  startDrawing(event, day)
                }}
                onPointerMove={keepDragging}
                onPointerUp={letGo}
                onPointerCancel={() => {
                  setDragging(null)
                }}
              >
                {/*
                The day name a second time, for the narrow screen where the columns stack into a
                list and the row of headings above them is gone. Exactly one of the two is ever in
                the document: the other is `display: none`, which takes it out of the reading order
                as well.
              */}
                <h3 className="cal__column-name">{readableDay(day)}</h3>

                <span
                  className="cal__daytime"
                  aria-hidden="true"
                  style={between(WORKING_FROM, WORKING_UNTIL)}
                />

                {laid.map((one) => (
                  <Slot
                    key={one.what.id}
                    laid={one}
                    open={one.what.id === openId}
                    dragged={dragging?.appointment?.id === one.what.id}
                    onGrab={(event, what) => {
                      startMoving(event, one.what, what)
                    }}
                    onPeek={(at) => {
                      if (!shifted.current) {
                        onPeek(one.what, at)
                      }
                    }}
                    onOpen={() => {
                      onOpen(one.what.id)
                    }}
                  />
                ))}

                {/*
                What is being dragged, where it would land.

                Both for moving and for making longer — the second had no preview at all before, so
                the lower edge could be pulled with nothing to show for it until the pointer was let
                go.
              */}
                {ghost !== null && (
                  <span
                    className="cal__ghost"
                    aria-hidden="true"
                    style={between(ghost.from, ghost.to)}
                  >
                    <span className="cal__ghost-time">
                      {clock(ghost.from)}
                      {'–'}
                      {clock(ghost.to)}
                    </span>
                    <span className="cal__ghost-title">{ghost.appointment?.title}</span>
                  </span>
                )}

                {/* What is being drawn, before it is anything: the same shape, without a name yet. */}
                {dragging !== null && dragging.what === 'draw' && dragging.day === day && (
                  <span
                    className="cal__draft"
                    aria-hidden="true"
                    style={between(
                      Math.min(dragging.from, dragging.to),
                      Math.max(Math.max(dragging.from, dragging.to), dragging.from + SNAP),
                    )}
                  >
                    {readableSpan(dragging)}
                  </span>
                )}

                {day === today && <span className="cal__now" aria-hidden="true" style={at(now)} />}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** Where an appointment sits, as it is stored. The layout never asks anything else. */
function stored(appointment: Appointment): { from: number; to: number } {
  return {
    from: minutesInDay(new Date(appointment.startsAt), appointment.zone),
    to: minutesInDay(new Date(appointment.endsAt), appointment.zone),
  }
}

function Slot({
  laid,
  open,
  dragged,
  onGrab,
  onPeek,
  onOpen,
}: {
  readonly laid: Laid<Appointment>
  readonly open: boolean
  readonly dragged: boolean
  readonly onGrab: (event: React.PointerEvent<HTMLElement>, what: 'move' | 'resize') => void
  readonly onPeek: (at: { x: number; y: number }) => void
  readonly onOpen: () => void
}) {
  const appointment = laid.what
  const about = whatItIsAbout(appointment)
  // Below three quarters of an hour there is room for one line and no more. A title cut off in the
  // middle of a word is worse than a title that stands alone.
  const room = laid.to - laid.from

  return (
    <button
      type="button"
      className="cal__slot"
      data-open={open}
      data-dragged={dragged}
      data-cancelled={appointment.cancelled}
      data-gone={appointment.goneFromSourceAt !== null}
      style={{
        ...between(laid.from, laid.to),
        left: `${String((laid.track / laid.tracks) * 100)}%`,
        width: `${String(100 / laid.tracks)}%`,
      }}
      onPointerDown={(event) => {
        onGrab(event, 'move')
      }}
      /*
        A single click asks what this is and is answered on the spot; a double click opens the page.
        A drag suppresses both, and nothing else does.

        The keyboard sends a click for Enter and for Space without any pointer at all, so it reaches
        the card — and the card carries the control that opens the page. No gesture here is the only
        way to anything.
      */
      onClick={(event) => {
        onPeek({ x: event.clientX, y: event.clientY })
      }}
      onDoubleClick={onOpen}
    >
      <span className="cal__slot-what">
        <span className="cal__slot-title">{appointment.title}</span>
        {room >= 45 && (
          <span className="cal__slot-time">
            {readableTime(new Date(appointment.startsAt), appointment.zone)}
            {'–'}
            {readableTime(new Date(appointment.endsAt), appointment.zone)}
          </span>
        )}
        {room >= 75 && about !== '' && about !== appointment.title && (
          <span className="cal__slot-about">{about}</span>
        )}
        {room >= 75 && appointment.room !== '' && (
          <span className="cal__slot-room">{appointment.room}</span>
        )}
        {appointment.cancelled && <span className="cal__slot-mark">Cancelled</span>}
        {/*
          What the source says is different about this one — a substitution, a room that moved. On
          the slot itself and not only in the card behind it, because the whole point of it is to be
          seen while looking at the week rather than after opening something.
        */}
        {!appointment.cancelled && appointment.remark !== '' && (
          <span className="cal__slot-mark cal__slot-mark--changed">{appointment.remark}</span>
        )}
      </span>
      <span
        className="cal__slot-handle"
        aria-hidden="true"
        onPointerDown={(event) => {
          onGrab(event, 'resize')
        }}
      />
    </button>
  )
}

/** A line at this minute of the day. */
function at(minute: number): React.CSSProperties {
  return { top: `calc(${String(minute)} * var(--minute))` } as React.CSSProperties
}

/** A block between these two minutes of the day. */
function between(from: number, to: number): React.CSSProperties {
  return {
    top: `calc(${String(from)} * var(--minute))`,
    height: `calc(${String(Math.max(SNAP, to - from))} * var(--minute))`,
  } as React.CSSProperties
}

function readableSpan(dragging: Dragging): string {
  const from = Math.min(dragging.from, dragging.to)
  const to = Math.max(Math.max(dragging.from, dragging.to), from + SNAP)
  return `${clock(from)}–${clock(to)}`
}

function clock(minute: number): string {
  return `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
}
